import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Settings, ArrowRightLeft, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ActionsCard({
  application,
  user,
  canTransferDepartment,
  canAssignOfficial,
  getCurrentPhase,
  getTargetDepartment,
  onTransferClick,
  onAssignOfficialClick,
}) {
  const { t } = useTranslation();

  // Don't render if user is not an official or application is finalized
  if (
    user?.role !== "official" ||
    application.status === "approved" ||
    application.status === "rejected"
  ) {
    return null;
  }

  // Don't render if user can't perform any actions
  if (!canTransferDepartment && !canAssignOfficial) {
    return null;
  }

  return (
    <Card className="p-6 bg-white border border-stone-200 shadow-sm">
      <h2 className="text-lg font-manrope font-bold text-emerald-700 mb-4 flex items-center gap-2">
        <Settings className="w-5 h-5" />
        {t("applicationDetail.actions")}
      </h2>

      <div className="space-y-3">
        {/* Info text about who can approve */}
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          {(application.department === "both" && getCurrentPhase() !== "revenue") ||
           application.department === "survey" || 
           ["ddlr", "survey"].includes(application.section) ? (
            <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
              <li>{t("status.surveyApprovalADLR")}</li>
              <li>{t("status.surveyApprovalDDLR")}</li>
            </ul>
          ) : (
            <p className="text-sm text-amber-800">
              {t("status.onlyTahsildarCanApprove")}
            </p>
          )}
        </div>

        {/* Assign to Official Button (Super Authorities) */}
        {canAssignOfficial && (
          <Button
            onClick={onAssignOfficialClick}
            className="w-full justify-start bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200"
            variant="outline"
            data-testid="assign-official-button"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {t("officialsManagement.assignApplication") || "Assign to Official"}
          </Button>
        )}

        {/* Cross-Department Transfer Button (Super Authorities Only) */}
        {canTransferDepartment && (
          <Button
            onClick={onTransferClick}
            className="w-full justify-start bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
            variant="outline"
            data-testid="transfer-department-button"
          >
            <ArrowRightLeft className="w-4 h-4 mr-2" />
            {t("applicationDetail.transferTo")}{" "}
            {getTargetDepartment() === "survey"
              ? t("department.survey")
              : t("department.revenue")}
          </Button>
        )}
      </div>
    </Card>
  );
}
